import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSource, useSources } from "../../../sources";
import { ServerSource } from "../../../sources/serverSource";
import { getPrefs, patchPrefs, type ServerPrefs } from "../../../lib/libraryAdmin";
import {
  fieldOfProblem,
  fmtTaskDuration,
  formFromPrefs,
  getTasks,
  HOUR_OPTIONS,
  canRunTask,
  libraryGatedTask,
  runTask,
  sameForm,
  stopTask,
  TASK_TOGGLES,
  tasksPatch,
  type TaskEntry,
  type TasksForm,
  validateForm,
  windowDisabled,
} from "../../../lib/tasksAdmin";
import { describeRun } from "../../../lib/troubleshootingAdmin";
import { formatBytes } from "../../../lib/transcoderAdmin";
import { useAdvancedSettings } from "../../../lib/settingsAdvanced";
import { useServerEvents } from "../../../lib/useServerEvents";
import {
  Checkbox,
  Chip,
  ErrorNote,
  errorMessage,
  Field,
  Select,
  Spinner,
} from "../../../components/ui";
import {
  AdvancedToggle,
  saveOnEnter,
  SheetHeader,
  SheetInset,
  SheetLoading,
  SheetNotice,
  SheetRow,
  SheetRows,
  SheetSection,
  UnsavedBar,
  useDraft,
  useLastTouched,
} from "../../../components/settings-sheet";
import { ServerSettingsGuard } from "../../../components/server-settings";

export const Route = createFileRoute("/_authed/settings/server/$serverId/tasks")({
  component: ServerScheduledTasks,
});

/** One task's live row: title, state chips, last-run line, Run now/Stop. */
function TaskRow({
  task,
  pending,
  onRun,
  onStop,
}: {
  task: TaskEntry;
  pending: boolean;
  onRun: () => void;
  onStop: () => void;
}) {
  const run = describeRun(task);
  const busy = task.running || task.queued;
  // A gated task that is off cannot be started (the server refuses it
  // too), but a run already in flight can still be stopped.
  const withheld = !busy && !canRunTask(task);
  const cadence = task.intervalDays === 7 ? "weekly" : `every ${task.intervalDays} days`;
  const toneClass =
    run?.tone === "warn"
      ? "text-[var(--rago-error)]"
      : run?.tone === "ok"
        ? "text-[var(--rago-text-2)]"
        : "text-[var(--rago-text-3)]";
  return (
    <SheetRow align="start">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-[var(--rago-text-1)]">{task.title}</span>
          {task.running ? (
            <Chip tone="accent">Running</Chip>
          ) : task.queued ? (
            <Chip tone="accent">Queued</Chip>
          ) : null}
          {!task.enabled ? <Chip tone="muted">Off</Chip> : null}
        </div>
        <p className="mt-0.5 text-xs text-[var(--rago-text-3)]">{task.description}</p>
        {libraryGatedTask(task.name) && !task.enabled ? (
          <p className="mt-0.5 text-xs text-[var(--rago-text-3)]">
            Turned off — enable it under Library › Media analysis.
          </p>
        ) : null}
        {run ? (
          <p className={`mt-0.5 text-xs ${toneClass}`}>
            {run.text}
            {run.tone === "ok" && task.lastDurationMs
              ? ` (${fmtTaskDuration(task.lastDurationMs)})`
              : ""}
          </p>
        ) : (
          <p className="mt-0.5 text-xs text-[var(--rago-text-3)]">
            Never run — scheduled {cadence}.
          </p>
        )}
      </div>
      <button
        type="button"
        className="rago-btn rago-btn-quiet rago-btn-sm shrink-0"
        disabled={pending || withheld}
        title={withheld ? "Turned off — enable it under Library › Media analysis." : undefined}
        onClick={busy ? onStop : onRun}
      >
        {pending ? <Spinner size={14} /> : null}
        {busy ? "Stop" : "Run now"}
      </button>
    </SheetRow>
  );
}

function ServerScheduledTasks() {
  const { serverId } = Route.useParams();
  // Subscribe to the registry: getSource() is a sync snapshot, and the
  // prefs query would stay disabled forever without the hydration rerender.
  useSources();
  const source = getSource(serverId);
  const isServer = source instanceof ServerSource;
  const qc = useQueryClient();
  const [advanced] = useAdvancedSettings();

  const prefs = useQuery({
    queryKey: ["settings", "prefs", serverId],
    enabled: isServer,
    queryFn: () => getPrefs((source as ServerSource).conn),
  });
  // Live task registry — read-only, independent of the prefs form. Older
  // servers 404 the route; the status card simply stays hidden then.
  const tasksQ = useQuery({
    queryKey: ["settings", "tasks", serverId],
    enabled: isServer,
    retry: false,
    queryFn: () => getTasks((source as ServerSource).conn),
  });

  // Live refresh: the runner publishes an owner-only "task" event at each
  // run start/end, so the status card follows Run now without polling.
  useServerEvents(isServer ? serverId : null, (ev) => {
    if (ev.type === "task") {
      void qc.invalidateQueries({ queryKey: ["settings", "tasks", serverId] });
    }
  });

  // One draft form seeded from the server's values, following them while it
  // is still clean (settings-sheet useDraft).
  const baseline = useMemo(
    () => (prefs.data ? formFromPrefs(prefs.data) : null),
    [prefs.data],
  );
  const [draft, setDraft] = useDraft(baseline, sameForm);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof TasksForm, string>>>({});
  const [pendingTask, setPendingTask] = useState<string | null>(null);
  // A Run now / Stop that failed: said beside the tasks, not by the pill —
  // its note is a failed save's.
  const [taskError, setTaskError] = useState<unknown>(null);
  // The control changed last: it gets the focus back when the pill leaves.
  const touched = useLastTouched();

  const form = draft ?? baseline;
  const patch = baseline && form && prefs.data ? tasksPatch(baseline, form, prefs.data) : {};
  const dirty = Boolean(baseline && form && !sameForm(baseline, form));

  // Feature detection: a server that predates the task runner has none of
  // the maintenance keys, and phantom toggles would look broken.
  const supported = Boolean(prefs.data && "maintenanceStartHour" in prefs.data);
  const has = (k: keyof ServerPrefs) => Boolean(prefs.data && k in prefs.data);

  function update(p: Partial<TasksForm>) {
    setDraft((f) => ({ ...(f as TasksForm), ...p }));
    setSaved(false);
    setError(null);
    setFieldErrors((e) => {
      const next = { ...e };
      for (const k of Object.keys(p) as (keyof TasksForm)[]) delete next[k];
      return next;
    });
  }

  function revert() {
    setDraft(baseline);
    setError(null);
    setFieldErrors({});
  }

  // The backup directory is the one field with a line of its own: a wrong
  // one takes the focus, which brings it into view under the floating pill.
  // Hidden with Show advanced, it has nowhere to say it: the pill's note.
  function showFieldErrors(errs: Partial<Record<keyof TasksForm, string>>) {
    setFieldErrors((prev) => ({ ...prev, ...errs }));
    const input = errs.backupDir ? document.getElementById("tasks-backup-dir") : null;
    if (input) input.focus();
    else setError(new Error(Object.values(errs)[0]));
  }

  async function save() {
    if (!isServer || !form || saving || !dirty) return;
    const errs = validateForm(form);
    if (Object.keys(errs).length > 0) {
      showFieldErrors(errs);
      return;
    }
    if (Object.keys(patch).length === 0) {
      // Nothing to send (a key this server lacks): the page is clean.
      setDraft(baseline);
      return;
    }
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await patchPrefs(source.conn, patch);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["settings", "prefs", serverId] }),
        qc.invalidateQueries({ queryKey: ["settings", "tasks", serverId] }),
      ]);
      setSaved(true);
    } catch (e) {
      const field = fieldOfProblem(e);
      const message =
        e && typeof e === "object" && "problem" in e
          ? ((e as { problem: { detail?: string; title?: string } }).problem.detail ??
            (e as { problem: { title?: string } }).problem.title)
          : undefined;
      if (field && message) {
        showFieldErrors({ [field]: message });
      } else {
        setError(e);
      }
    } finally {
      setSaving(false);
    }
  }

  async function taskAction(task: TaskEntry) {
    if (!isServer) return;
    setPendingTask(task.name);
    setTaskError(null);
    try {
      if (task.running || task.queued) {
        await stopTask(source.conn, task.name);
      } else {
        await runTask(source.conn, task.name);
      }
      await qc.invalidateQueries({ queryKey: ["settings", "tasks", serverId] });
    } catch (e) {
      setTaskError(e);
    } finally {
      setPendingTask(null);
    }
  }

  const backup = tasksQ.data?.backup;
  const backupHint = (() => {
    const parts = [
      'Where database backups are stored. Leave empty for the default. Never a media folder.',
    ];
    if (backup?.error) parts.push(`Set path unusable (${backup.error}) — using the default.`);
    else if (backup?.freeBytes) parts.push(`${formatBytes(backup.freeBytes)} free.`);
    return parts.join(" ");
  })();

  return (
    <ServerSettingsGuard serverId={serverId} heading="Scheduled Tasks">
      {(server) => (
        // .rago-sheet-fill: the root fills the sheet, so the pill docks at its foot.
        <div className="rago-sheet-fill">
          <SheetHeader
            title="Scheduled Tasks"
            action={supported ? <AdvancedToggle /> : null}
          />

          {prefs.isLoading || !form ? (
            // A load that failed says so below instead of spinning for good.
            prefs.error ? null : (
              <SheetLoading />
            )
          ) : !supported ? (
            <SheetNotice title="Scheduled Tasks">
              This server doesn't support scheduled tasks yet — update rago-server.
            </SheetNotice>
          ) : (
            // Every control's change bubbles to here: the one changed last.
            <div onChange={touched.onChange}>
              <SheetSection
                title="Maintenance window"
                action={tasksQ.data?.window.open ? <Chip tone="accent">Open now</Chip> : undefined}
              >
                <Field
                  label="Time at which tasks start to run"
                  htmlFor="tasks-start-hour"
                  hint="The server starts running background maintenance tasks at this hour, in its local time."
                >
                  <Select
                    id="tasks-start-hour"
                    value={String(form.startHour)}
                    onChange={(v) => update({ startHour: Number(v) })}
                  >
                    {HOUR_OPTIONS.map(([h, label]) => (
                      <option key={h} value={h}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field
                  label="Time at which tasks stop running"
                  htmlFor="tasks-end-hour"
                  hint={
                    windowDisabled(form)
                      ? "Start and stop are the same hour, so scheduled tasks never run. Pick a 2–3 hour window."
                      : "Unfinished tasks resume the next night. A window past midnight (e.g. 22:00 – 5:00) is fine."
                  }
                >
                  <Select
                    id="tasks-end-hour"
                    value={String(form.endHour)}
                    onChange={(v) => update({ endHour: Number(v) })}
                  >
                    {HOUR_OPTIONS.map(([h, label]) => (
                      <option key={h} value={h}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>
              </SheetSection>

              <SheetSection title="Tasks">
                {TASK_TOGGLES.map((toggle, i) => (
                  <div key={toggle.field}>
                    <div className={i === TASK_TOGGLES.length - 1 ? "" : "mb-4"}>
                      <Checkbox
                        id={`task-${toggle.field}`}
                        checked={form[toggle.field]}
                        onChange={(v) => update({ [toggle.field]: v } as Partial<TasksForm>)}
                        label={toggle.label}
                        hint={toggle.hint}
                      />
                    </div>
                    {toggle.field === "backupDatabase" && advanced && has("maintenanceBackupDir") ? (
                      <div className="mb-4">
                        <Field
                          label="Backup directory"
                          htmlFor="tasks-backup-dir"
                          hint={backupHint}
                          error={fieldErrors.backupDir}
                        >
                          <input
                            id="tasks-backup-dir"
                            className="rago-input"
                            value={form.backupDir}
                            placeholder={backup?.default ?? "Server default"}
                            spellCheck={false}
                            autoCapitalize="off"
                            autoCorrect="off"
                            onChange={(e) => update({ backupDir: e.target.value })}
                            onKeyDown={saveOnEnter(() => void save())}
                          />
                        </Field>
                      </div>
                    ) : null}
                  </div>
                ))}
              </SheetSection>

              {tasksQ.data ? (
                <SheetSection title="Task status" flush>
                  {taskError ? (
                    <SheetInset>
                      <ErrorNote error={taskError} />
                    </SheetInset>
                  ) : null}
                  <SheetRows>
                    {tasksQ.data.items.map((task) => (
                      <TaskRow
                        key={task.name}
                        task={task}
                        pending={pendingTask === task.name}
                        onRun={() => void taskAction(task)}
                        onStop={() => void taskAction(task)}
                      />
                    ))}
                  </SheetRows>
                </SheetSection>
              ) : null}
            </div>
          )}

          {prefs.error ? (
            <SheetInset>
              <ErrorNote error={prefs.error} />
            </SheetInset>
          ) : null}

          {/* The root's direct child (its dock rides at the sheet's foot), and
              always rendered, as UnsavedBar asks. */}
          <UnsavedBar
            dirty={dirty}
            saving={saving}
            saved={saved}
            error={error ? errorMessage(error) : null}
            onSave={() => void save()}
            onRevert={revert}
            onFocusLost={touched.focus}
          />
        </div>
      )}
    </ServerSettingsGuard>
  );
}
